import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  CheckCircle,
  RadioButtonUnchecked,
  Close,
  ChevronRight,
  CheckCircleOutline,
  Replay,
  HelpOutline,
  Undo,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { checklistItems } from "../../config/tourSteps";
import ChecklistHelpModal from "./ChecklistHelpModal";
import ContextualGuidance from "./ContextualGuidance";
import WelcomeModal from "./WelcomeModal";

const OnboardingChecklist = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false); // Start minimized
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationShown, setCelebrationShown] = useState(false); // Track if celebration has been shown
  const [dismissed, setDismissed] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [selectedHelpContent, setSelectedHelpContent] = useState(null);
  const [activeGuidance, setActiveGuidance] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Get checklist state from Redux
  const tourProgress = useSelector((state) => state.user?.tourProgress);
  const checklistState = tourProgress?.checklist_items || {};

  // Get user tier from Redux (populated on login)
  const isRiskFreeTier = useSelector((state) => state.user?.isRiskFreeTier);

  // Filter checklist items based on tier
  const itemsToHideForRiskFree = [
    "setup_payment_connection",
    "setup_landing_page",
    "setup_availability",
    "schedule_first_session",
  ];

  const filteredChecklistItems = checklistItems.filter((item) => {
    if (isRiskFreeTier && itemsToHideForRiskFree.includes(item.key)) {
      return false;
    }
    return true;
  });

  // Calculate completion percentage based on filtered items
  const completedCount = filteredChecklistItems.filter((item) => checklistState[item.key]).length;
  const totalCount = filteredChecklistItems.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  // Check if user previously dismissed the checklist
  useEffect(() => {
    const wasDismissed = localStorage.getItem("checklistDismissed");
    if (wasDismissed === "true") {
      setDismissed(true);
    }
  }, []);

  // Show welcome modal on first visit
  useEffect(() => {
    const hasSeenWelcome = localStorage.getItem("hasSeenWelcome");
    const wasDismissed = localStorage.getItem("checklistDismissed");

    // Show welcome modal if user hasn't seen it, hasn't dismissed the checklist, and has tour progress
    if (!hasSeenWelcome && !wasDismissed && tourProgress && progress < 100) {
      setShowWelcomeModal(true);
      setExpanded(false); // Keep checklist minimized when welcome modal appears
    } else if (hasSeenWelcome && !wasDismissed && progress < 100) {
      // If user has seen welcome but hasn't dismissed, show checklist expanded
      setExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourProgress, progress]);

  useEffect(() => {
    // Show celebration when all items are completed
    if (completedCount === totalCount && completedCount > 0 && !dismissed && !celebrationShown) {
      setShowCelebration(true);
      setCelebrationShown(true);
    }
  }, [completedCount, totalCount, dismissed, celebrationShown]);

  useEffect(() => {
    // Load and display guidance after navigation
    const savedGuidance = sessionStorage.getItem("activeGuidance");
    if (savedGuidance) {
      try {
        const guidanceData = JSON.parse(savedGuidance);
        // Wait for the page to render before showing guidance
        setTimeout(() => {
          if (guidanceData.guidance && guidanceData.guidance.length > 0) {
            setActiveGuidance(guidanceData.guidance[guidanceData.currentIndex]);
          }
          sessionStorage.removeItem("activeGuidance");
        }, 500); // 500ms delay to ensure page elements are rendered
      } catch (error) {
        console.error("Error loading guidance:", error);
        sessionStorage.removeItem("activeGuidance");
      }
    }
  }, [location.pathname, location.state]); // Watch both pathname and state changes

  const handleItemClick = (item) => {
    if (item.route) {
      // Minimize the checklist when navigating
      setExpanded(false);

      // Save guidance data to sessionStorage to display after navigation
      if (item.guidance && item.guidance.length > 0) {
        sessionStorage.setItem(
          "activeGuidance",
          JSON.stringify({
            guidance: item.guidance,
            currentIndex: 0,
          })
        );
      }

      // Navigate to specific tabs in Settings
      if (item.key === "complete_profile") {
        // Account tab (index 0)
        navigate(item.route, { state: { tabIndex: 0 } });
      } else if (item.key === "setup_communication") {
        // Communication tab (index 1)
        navigate(item.route, { state: { tabIndex: 1 } });
      } else if (item.key === "complete_setup") {
        // Set Up tab (index 2)
        navigate(item.route, { state: { tabIndex: 2 } });
      } else if (item.key === "setup_payment_connection") {
        // Payment Connection tab (index 3)
        navigate(item.route, { state: { tabIndex: 3 } });
      } else {
        navigate(item.route);
      }
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Optionally save dismissal preference to localStorage or backend
    localStorage.setItem("checklistDismissed", "true");
  };

  const handleWelcomeSkip = () => {
    setShowWelcomeModal(false);
    localStorage.setItem("hasSeenWelcome", "true");
    localStorage.setItem("checklistDismissed", "true");
    // Dismiss the checklist entirely - set state immediately
    setDismissed(true);
  };

  const handleWelcomeProceed = () => {
    setShowWelcomeModal(false);
    localStorage.setItem("hasSeenWelcome", "true");
    // Keep the checklist visible and expanded
    setExpanded(true);
  };

  const handleCelebrationClose = () => {
    setShowCelebration(false);
    // Dismiss the checklist when celebration is closed
    setDismissed(true);
    localStorage.setItem("checklistDismissed", "true");
  };

  const handleReplayTour = async () => {
    try {
      // Import necessary modules
      const { TourActions } = await import("../api/RequestUtil");
      const { setTourProgress } = await import("../../redux/features/userSlice");
      const { store } = await import("../../redux/store");

      // Reset tour on backend
      await TourActions.resetTour();

      // Clear dismissal and welcome flags to show everything again
      localStorage.removeItem("checklistDismissed");
      localStorage.removeItem("hasSeenWelcome");

      // Fetch updated tour progress
      const response = await TourActions.getTourProgress();
      if (response.data?.status === "success") {
        // Update Redux with reset tour progress
        store.dispatch(setTourProgress(response.data.data));

        // Reload page to restart tour
        window.location.reload();
      }
    } catch (error) {
      console.error("Error replaying tour:", error);
    }
  };

  const handleManualComplete = async (e, itemKey) => {
    e.stopPropagation(); // Prevent navigation when clicking the button

    try {
      // Import TourActions and updateChecklistItems from the correct location
      const { TourActions } = await import("../api/RequestUtil");
      const { updateChecklistItems } = await import("../../redux/features/userSlice");
      const { store } = await import("../../redux/store");

      // Update via API
      await TourActions.updateChecklistItems({ [itemKey]: true });

      // Update Redux
      store.dispatch(updateChecklistItems({ [itemKey]: true }));

      // Check if this was the last item to complete
      const updatedChecklistState = { ...checklistState, [itemKey]: true };
      const newCompletedCount = filteredChecklistItems.filter((item) => updatedChecklistState[item.key]).length;

      if (newCompletedCount === filteredChecklistItems.length && filteredChecklistItems.length > 0) {
        // All items are now complete, show celebration
        setTimeout(() => setShowCelebration(true), 300); // Small delay for smooth transition
      }
    } catch (error) {
      console.error("Error manually completing checklist item:", error);
    }
  };

  const handleUndoComplete = async (e, itemKey) => {
    e.stopPropagation(); // Prevent navigation when clicking the button

    try {
      // Import TourActions and updateChecklistItems from the correct location
      const { TourActions } = await import("../api/RequestUtil");
      const { updateChecklistItems } = await import("../../redux/features/userSlice");
      const { store } = await import("../../redux/store");

      // Update via API - set to false
      await TourActions.updateChecklistItems({ [itemKey]: false });

      // Update Redux
      store.dispatch(updateChecklistItems({ [itemKey]: false }));
    } catch (error) {
      console.error("Error undoing checklist item:", error);
    }
  };

  const handleHelpClick = (e, helpContent) => {
    e.stopPropagation(); // Prevent navigation when clicking help button
    setSelectedHelpContent(helpContent);
    setHelpModalOpen(true);
  };

  const handleHelpModalClose = () => {
    setHelpModalOpen(false);
    setSelectedHelpContent(null);
  };

  // Don't show if tour is not started or checklist is dismissed
  if (!tourProgress || dismissed) {
    return null;
  }

  // If progress is 100%, only render the celebration modal (not the checklist UI)
  const shouldShowChecklistUI = progress < 100;

  return (
    <>
      {/* Welcome Modal */}
      <WelcomeModal open={showWelcomeModal} onSkip={handleWelcomeSkip} onProceed={handleWelcomeProceed} />

      {/* Only show checklist UI if progress is not 100% */}
      {shouldShowChecklistUI && (
        <>
          {/* Minimized Tab */}
          {!expanded && (
            <Box
              onClick={() => {
                setExpanded(true);
                setActiveGuidance(null); // Clear any active guidance when expanding checklist
              }}
              sx={{
                position: "fixed",
                top: "50%",
                right: 0,
                transform: "translateY(-50%)",
                bgcolor: "primary.main",
                py: 3,
                px: 1.5,
                borderTopLeftRadius: 8,
                borderBottomLeftRadius: 8,
                cursor: "pointer",
                boxShadow: 3,
                zIndex: 9999,
                transition: "all 0.3s ease",
                "&:hover": {
                  bgcolor: "primary.dark",
                  px: 2,
                },
                writingMode: "vertical-rl",
                textOrientation: "mixed",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  letterSpacing: 1,
                  color: "#ffffff !important",
                }}
              >
                GETTING STARTED {Math.round(progress)}%
              </Typography>
            </Box>
          )}

          {/* Expanded Sidebar */}
          <Box
            sx={{
              position: "fixed",
              top: 0,
              right: expanded ? 0 : "-400px",
              height: "100vh",
              width: 360,
              bgcolor: "primary.main",
              boxShadow: expanded ? "-2px 0 8px rgba(0,0,0,0.15)" : "none",
              zIndex: 9998,
              transition: "right 0.3s ease",
              display: "flex",
              flexDirection: "column",
              borderLeft: "1px solid",
              borderColor: "primary.dark",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600, color: "#ffffff" }}>
                  Getting Started
                </Typography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setExpanded(false)}
                    aria-label="minimize"
                    sx={{ color: "#ffffff" }}
                  >
                    <ChevronRight />
                  </IconButton>
                  <IconButton size="small" onClick={handleDismiss} aria-label="dismiss" sx={{ color: "#ffffff" }}>
                    <Close />
                  </IconButton>
                </Box>
              </Box>

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#ffffff" }}>
                    {completedCount} of {totalCount} completed
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#ffffff" }}>
                    {Math.round(progress)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: "rgba(255, 255, 255, 0.2)",
                    "& .MuiLinearProgress-bar": {
                      bgcolor: "lemon.light",
                      borderRadius: 4,
                    },
                  }}
                />
              </Box>

              {/* Replay Tour Button */}
              <Button
                size="small"
                startIcon={<Replay />}
                onClick={handleReplayTour}
                sx={{
                  mt: 2,
                  textTransform: "none",
                  fontSize: "0.875rem",
                  color: "#ffffff",
                  borderColor: "rgba(255, 255, 255, 0.3)",
                  "&:hover": {
                    borderColor: "#ffffff",
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
                variant="outlined"
              >
                Replay Tour
              </Button>
            </Box>

            {/* Scrollable Content */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                "&::-webkit-scrollbar": {
                  width: "6px",
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(255, 255, 255, 0.3)",
                  borderRadius: "3px",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.5)",
                },
              }}
            >
              <List sx={{ p: 0 }}>
                {filteredChecklistItems.map((item) => {
                  const isCompleted = checklistState[item.key] || false;
                  return (
                    <ListItem
                      key={item.key}
                      sx={{
                        borderRadius: 1,
                        mb: 1.5,
                        bgcolor: "rgba(255, 255, 255, 0.1)",
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.15)",
                          "& .manual-complete-btn": {
                            opacity: 1,
                          },
                          "& .undo-complete-btn": {
                            opacity: 1,
                          },
                        },
                        opacity: isCompleted ? 0.7 : 1,
                        position: "relative",
                        py: 1.5,
                        px: 2,
                        alignItems: "flex-start",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                        {isCompleted ? (
                          <CheckCircle sx={{ fontSize: 18, color: "lemon.light" }} />
                        ) : (
                          <RadioButtonUnchecked sx={{ color: "rgba(255, 255, 255, 0.5)" }} />
                        )}
                      </ListItemIcon>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {/* Title row with icon */}
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, cursor: "pointer" }}
                          onClick={() => handleItemClick(item)}
                        >
                          <Box
                            component="span"
                            sx={{
                              color: "lemon.light",
                              bgcolor: "lemon.light",
                              borderRadius: "50%",
                              width: "28px",
                              height: "28px",
                              minWidth: "28px",
                              minHeight: "28px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              "& svg": {
                                fontSize: "16px",
                                color: "primary.main",
                              },
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              textDecoration: isCompleted ? "line-through" : "none",
                              fontWeight: isCompleted ? 400 : 600,
                              fontSize: "14px",
                              lineHeight: 1.5,
                              color: "#ffffff",
                            }}
                          >
                            {item.title}
                          </Typography>
                        </Box>
                        {/* Description row with help icon aligned */}
                        {!isCompleted && (
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, pl: 4 }}>
                            <Typography
                              variant="caption"
                              sx={{
                                flex: 1,
                                lineHeight: 1.4,
                                fontSize: "12px",
                                color: "rgba(255, 255, 255, 0.8)",
                              }}
                            >
                              {item.description}
                            </Typography>
                            {/* Help icon inline with description */}
                            {item.help && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleHelpClick(e, item.help)}
                                sx={{
                                  p: 0.5,
                                  ml: 0.5,
                                  color: "lemon.light",
                                }}
                                title="Get help with this step"
                              >
                                <HelpOutline fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                        {/* Help icon for completed items */}
                        {isCompleted && item.help && (
                          <Box sx={{ pl: 4, mt: 0.5 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleHelpClick(e, item.help)}
                              sx={{
                                p: 0.5,
                                color: "lemon.light",
                              }}
                              title="Get help with this step"
                            >
                              <HelpOutline fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                      {/* Manual complete button - top right for incomplete items */}
                      {!isCompleted && (
                        <IconButton
                          className="manual-complete-btn"
                          size="small"
                          onClick={(e) => handleManualComplete(e, item.key)}
                          sx={{
                            opacity: 0,
                            transition: "opacity 0.2s",
                            ml: 1,
                            alignSelf: "center",
                            color: "lemon.light",
                          }}
                          title="Mark as complete"
                        >
                          <CheckCircleOutline fontSize="small" />
                        </IconButton>
                      )}
                      {/* Undo button - top right for completed items */}
                      {isCompleted && (
                        <IconButton
                          className="undo-complete-btn"
                          size="small"
                          onClick={(e) => handleUndoComplete(e, item.key)}
                          sx={{
                            opacity: 0,
                            transition: "opacity 0.2s",
                            ml: 1,
                            alignSelf: "center",
                            color: "lemon.light",
                          }}
                          title="Undo completion"
                        >
                          <Undo fontSize="small" />
                        </IconButton>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>
        </>
      )}

      {/* Celebration Dialog */}
      <Dialog
        open={showCelebration}
        onClose={handleCelebrationClose}
        maxWidth="sm"
        fullWidth
        sx={{
          zIndex: 10000, // Higher than the checklist sidebar (9998)
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pt: 4 }}>
          <Typography variant="h3" sx={{ mb: 2 }}>
            🎉
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Congratulations!
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", pb: 2 }}>
          <Typography variant="body1" color="text.secondary">
            You&apos;ve completed all onboarding tasks! You&apos;re all set to make the most of Nativus Coach.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button variant="contained" onClick={handleCelebrationClose} size="large">
            Let&apos;s Go!
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help Modal */}
      <ChecklistHelpModal open={helpModalOpen} onClose={handleHelpModalClose} helpContent={selectedHelpContent} />

      {/* Contextual Guidance */}
      {activeGuidance && (
        <ContextualGuidance
          target={activeGuidance.target}
          message={activeGuidance.message}
          onClose={() => setActiveGuidance(null)}
        />
      )}
    </>
  );
};

export default OnboardingChecklist;
